import { useId, useMemo, useState } from 'react';
import { INPUT_CLASS } from './Field';

export interface ComboboxItem {
  value: string;
  label: string;
  meta?: string;
}

interface ComboboxProps {
  id: string;
  items: ComboboxItem[];
  value: string;
  onChange(value: string): void;
  placeholder?: string;
  disabled?: boolean;
  emptyText?: string;
}

// A text input that filters its own list. A native select cannot be typed into, and the
// tournament lists run to hundreds of entries. The field text is local state: a parent that
// wants to reset it (a fresh form) remounts the combobox with a new `key`.
export function Combobox({
  id,
  items,
  value,
  onChange,
  placeholder,
  disabled,
  emptyText = 'Nothing matches',
}: ComboboxProps) {
  const listId = useId();
  const selected = useMemo(() => items.find((item) => item.value === value), [items, value]);
  const [text, setText] = useState(selected?.label ?? '');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);

  // A chosen item leaves its own label in the field; reopening should still show every option.
  const term = selected && text === selected.label ? '' : text.trim().toLowerCase();
  const matches = useMemo(
    () =>
      items.filter((item) => !term || `${item.label} ${item.value} ${item.meta ?? ''}`.toLowerCase().includes(term)),
    [items, term],
  );

  function pick(item: ComboboxItem) {
    onChange(item.value);
    setText(item.label);
    setOpen(false);
    setActive(-1);
  }

  function move(step: number) {
    if (!open) setOpen(true);
    if (!matches.length) return;
    setActive((current) => (current + step + matches.length) % matches.length);
  }

  return (
    <div className="relative">
      <input
        id={id}
        type="text"
        role="combobox"
        aria-expanded={open}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={active >= 0 ? `${listId}-${active}` : undefined}
        autoComplete="off"
        className={INPUT_CLASS}
        placeholder={placeholder}
        disabled={disabled}
        value={text}
        onChange={(event) => {
          setText(event.target.value);
          if (value) onChange('');
          setOpen(true);
          setActive(-1);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        onKeyDown={(event) => {
          if (event.key === 'ArrowDown') {
            event.preventDefault();
            move(1);
          } else if (event.key === 'ArrowUp') {
            event.preventDefault();
            move(-1);
          } else if (event.key === 'Enter' && open && active >= 0) {
            event.preventDefault();
            pick(matches[active]);
          } else if (event.key === 'Escape') {
            setOpen(false);
          }
        }}
      />
      {open && !disabled && (
        <ul
          id={listId}
          role="listbox"
          className="absolute left-0 right-0 top-full z-10 mt-1 max-h-64 overflow-y-auto rounded-md border border-slate-200 bg-white py-1 shadow-lg"
        >
          {matches.length === 0 && <li className="px-3 py-1.5 text-sm text-slate-500">{emptyText}</li>}
          {matches.map((item, index) => (
            <li
              key={item.value}
              id={`${listId}-${index}`}
              role="option"
              aria-selected={item.value === value}
              className={`cursor-pointer px-3 py-1.5 text-sm ${index === active ? 'bg-blue-50' : ''} ${item.value === value ? 'font-semibold' : ''}`}
              // mousedown, not click: blur would close the list before a click could land.
              onMouseDown={(event) => {
                event.preventDefault();
                pick(item);
              }}
              onMouseEnter={() => setActive(index)}
            >
              <span className="block">{item.label}</span>
              {item.meta && <span className="block text-xs text-slate-500">{item.meta}</span>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
